import { Builder } from '@builder.io/sdk';
import { BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES } from '../BuilderComponentsName';

import {
  textColorOptionsLimited,
  backgroundGradientOptions,
  waveColorOptions,
  altBackgroundColorOptions
} from '../utils/builderColorOptions';
import { getBackgroundGradientInput, getPaddingInputs, getBackgroundColorInput, getWaveGradientInput } from '../utils/builderSharedInputs';
import { S16BackgroundImageSection } from '../components/interactive-sections/S16BackgroundImage';

Builder.registerComponent(S16BackgroundImageSection, {
  name: BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S16BackgroundImage,
  friendlyName: 'S16 Background Image',
  inputs: [
    {
      name: 'imageSrc',
      type: 'file',
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F517b116ccdea432ca852b3b2774d8c43',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg'],
      friendlyName: 'Image',
      helperText: 'Upload an image or provide an image URL for the section',
      required: true,
    },
    {
      name: 'imageAltText',
      type: 'string',
      friendlyName: 'Image Alt Text',
      defaultValue: 'Image Alt Text',
      helperText: 'Alt text for the image',
      required: true,
    },
    {
      name: 'imagePosition',
      type: 'boolean',
      friendlyName: 'Image Position',
      defaultValue: false,
      helperText: 'Toggle to position image from left to right',
    },
    {
      name: 'headlineColor',
      type: 'string',
      friendlyName: 'Headline Color',
      enum: textColorOptionsLimited,
      defaultValue: 'Blue',
      helperText: 'Select a color for the headline text'
    },
    {
      name: 'bodyTextColor',
      type: 'string',
      friendlyName: 'Body Text Color',
      enum: textColorOptionsLimited,
      defaultValue: 'Navy',
      helperText: 'Select a color for the body text content'
    },
    {
      name: "subsections",
      type: "list",
      friendlyName: "Subsections",
      helperText: "Add subsections with heading and body content",
      subFields: [
        {
          name: "heading",
          type: "string",
          friendlyName: "Heading",
          required: true,
          helperText: "The heading for this subsection. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}",
          defaultValue: "Subsection Heading"
        },
        {
          name: "body",
          type: "string",
          friendlyName: "Body",
          required: true,
          helperText: "The body content for this subsection. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}",
          defaultValue: "Subsection body content goes here."
        }
      ],
      defaultValue: [
        {
          heading: "Personalized treatments, permanent results.",
          body: "Our experts create a treatment plan for each client based on their skin type and hair color. The Alexandrite laser is very effective for fair to light skin tones. The Nd:YAG is the best option for darker skin tones, as it penetrates deeper into the skin to target hair follicles."
        },
        {
          heading: "Head-to-toe treatments.",
          body: "We treat all areas from the eyebrows to the toes! The laser's large spot size makes it effective for larger areas like the back, chest, or legs quickly and efficiently. It can precisely target small areas like the upper lip, toes, and unibrow."
        },
        {
          heading: "Your safety first.",
          body: "The GentleMax Pro's technology and safety features protects the surrounding skin and ensures the laser energy focuses on the hair follicle, minimizing the risk of side effects."
        }
      ]
    },
    {
      name: "hasBackgroundWave",
      type: "boolean",
      friendlyName: "Show Background Wave",
      defaultValue: true,
      helperText: "Toggle to show or hide the background wave. This will not work if background gradient is selected.",
    },
    {
      name: "flipWave",
      type: "boolean",
      friendlyName: "Flip Background Wave",
      defaultValue: false,
      helperText: "Toggle to flip the background wave direction.",
    },
    {
      name: "backgroundWaveColor",
      type: "string",
      friendlyName: "Background Wave Color",
      enum: waveColorOptions,
      defaultValue: "White",
      helperText: "Select the color for the background wave.",
    },
    ...getWaveGradientInput({
      waveGradientList: backgroundGradientOptions,
      helperText: 'Gradient for wave effect. Requires wave to be enabled. Set Background Color and Background Gradient to None if using this.'
  }),
  ...getBackgroundColorInput({
      backgroundColorsList: altBackgroundColorOptions,
      defaultValue: 'Blue10',
      helperText: 'Solid background color. Set Background Gradient to None if using this.'
  }),
  ...getBackgroundGradientInput({
      backgroundGradientList: backgroundGradientOptions,
      helperText: 'Background gradient without wave. Set Background Color to None if using this.'
  }),
  {
      name: 'backgroundImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      friendlyName: 'Background Image',
      helperText: 'Upload an image to use as the section background'
  },
    ...getPaddingInputs({
      desktopTop: 'xlarge',
      desktopBottom: 'none',
      mobileTop: 'small',
      mobileBottom: 'none',
    }),
  ]
});
